import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Intro } from "../components/simulator/Intro";
import { CompanyForm } from "../components/simulator/CompanyForm";
import { YearWizard } from "../components/simulator/YearWizard";
import { Results } from "../components/simulator/Results";
import { InvalidLink } from "./InvalidLink";

const emptyCompany = {
  referente: "", ragione_sociale: "", partita_iva: "", email: "", cellulare: "",
  settore: "", tipo_utente: "", ruolo: "", ha_revisore: false, ha_durc: false,
};

export const Simulator = ({ cobrand = false }) => {
  const { slug } = useParams();
  const location = useLocation();
  const prefill = location.state?.prefill;
  const channelSlug = cobrand ? "assocamuna" : (slug || "diretto");
  const [channelValid, setChannelValid] = useState(null);
  const [step, setStep] = useState(prefill ? "company" : "intro");
  const [company, setCompany] = useState(prefill?.company || emptyCompany);
  const [wizardData, setWizardData] = useState(prefill?.years || null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/canale/${channelSlug}`)
      .then((r) => setChannelValid(r.data.valid))
      .catch(() => setChannelValid(false));
  }, [channelSlug]);

  const getUtm = () => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || null,
      utm_medium: p.get("utm_medium") || null,
      utm_campaign: p.get("utm_campaign") || null,
    };
  };

  const confirm = async (anni, rawData) => {
    setWizardData(rawData);
    setSubmitting(true);
    try {
      const payload = {
        ...company,
        cellulare: company.cellulare || null,
        channel_slug: channelSlug,
        cobrand,
        ...getUtm(),
        anni,
      };
      const r = await api.post("/public/simulazioni", payload);
      setResult(r.data);
      setStep("results");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (channelValid === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#6C8EAD]" />
      </div>
    );
  if (!channelValid) return <InvalidLink />;

  return (
    <div className="min-h-screen bg-[#F5F8F7]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {cobrand && (
          <div className="mb-6" data-testid="cobrand-header">
            <div className="flex items-center justify-between gap-4">
              <img src="/logo-studio-scala-milano.png" alt="Studio Scala Milano"
                className="h-14 w-auto max-w-[45%] object-contain" />
              <img src="/logo-assocamuna.png" alt="Assocamuna"
                className="h-14 w-auto max-w-[50%] object-contain" />
            </div>
            <p className="text-center text-sm text-[#5B6E78] mt-3">In collaborazione con Assocamuna</p>
          </div>
        )}
        <div className="bg-white rounded-3xl border border-[#DDE5E2] shadow-sm p-6 sm:p-10">
          {step === "intro" && <Intro onStart={() => setStep("company")} />}
          {step === "company" && (
            <CompanyForm value={company} onBack={() => setStep("intro")}
              onNext={(f) => { setCompany(f); setStep("wizard"); window.scrollTo(0, 0); }} />
          )}
          {step === "wizard" && (
            <YearWizard value={wizardData} submitting={submitting}
              onBack={() => setStep("company")} onConfirm={confirm} />
          )}
          {step === "results" && result && <Results result={result} />}
        </div>
        <p className="text-center text-xs text-[#5B6E78] mt-6">Studio Scala Milano — studioscalamilano@yahoo.com</p>
      </div>
    </div>
  );
};
